import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  Psychology,
  Speed,
  Analytics,
  Refresh,
  Article,
  ShowChart,
} from '@mui/icons-material';
import TradingViewTickerTape from './TradingViewTickerTape';
import LiveMarketPrice from './LiveMarketPrice';
import TechnicalAnalysis from './TechnicalAnalysis';
import PriceChart from './PriceChart';
import MLPredictionPanel from './MLPredictionPanel';
import useMlPredictions from '../hooks/useMlPredictions';

const XAUUSDPredictor = () => {
  const { data: mlData, loading: mlLoading, reload } = useMlPredictions();
  const [refreshing, setRefreshing] = React.useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await reload();
    setRefreshing(false);
  };

  return (
    <Box>
      <TradingViewTickerTape />

      <Box textAlign="center" mb={3}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold" color="primary.main">
          XAUUSD Gold Predictor
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Live market + ML · Day-end close & next-day price
        </Typography>
        <Button
          variant="contained"
          startIcon={refreshing || mlLoading ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
          onClick={handleRefresh}
          disabled={refreshing || mlLoading}
          sx={{ mt: 1 }}
        >
          Refresh Predictions
        </Button>
        {mlData?.generatedAt && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            Model file: {new Date(mlData.generatedAt).toLocaleString()}
            {mlData?.live?.price != null && ` · Live in file: $${mlData.live.price.toLocaleString()}`}
          </Typography>
        )}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            <ShowChart sx={{ mr: 1, verticalAlign: 'middle' }} />
            Live Market Price
          </Typography>
          <LiveMarketPrice />
        </CardContent>
      </Card>

      {/* TODAY */}
      <Card sx={{ mb: 3, border: '2px solid', borderColor: 'secondary.main' }}>
        <CardContent>
          <MLPredictionPanel mode="today" />
        </CardContent>
      </Card>

      {/* TOMORROW */}
      <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
        <CardContent>
          <MLPredictionPanel mode="tomorrow" />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            <Analytics sx={{ mr: 1, verticalAlign: 'middle' }} />
            Technical Analysis
          </Typography>
          <TechnicalAnalysis />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
            Live Chart (TradingView)
          </Typography>
          <PriceChart />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, border: '1px dashed', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            <Article sx={{ mr: 1, verticalAlign: 'middle' }} />
            News Impact
          </Typography>
          <Alert severity="info">
            Coming next: Fed / CPI / geopolitics news impact on XAUUSD.
          </Alert>
        </CardContent>
      </Card>

      <Box textAlign="center" pb={2}>
        <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1}>
          <Chip icon={<Psychology />} label="ML Forecast" color="primary" variant="outlined" size="small" />
          <Chip icon={<Analytics />} label="Technical" color="secondary" variant="outlined" size="small" />
          <Chip icon={<Speed />} label="Live Spot" color="primary" variant="outlined" size="small" />
          <Chip icon={<Article />} label="News soon" variant="outlined" size="small" />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Educational only · Not financial advice
        </Typography>
      </Box>
    </Box>
  );
};

export default XAUUSDPredictor;
